import {
  FieldSection,
  type AdminItemType,
  type AdminMeta,
  type ItemTypeField,
  type LocalizedName,
} from '@numistic/contracts'
import { useTranslation } from '@numistic/i18n'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Field,
  Input,
  Select,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '@numistic/ui'
import { Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

import {
  ActiveToggle,
  AdminError,
  NameInputs,
  nextKey,
  OriginBadge,
  RemoveButton,
  UsageBadge,
  useAdminAction,
} from './common.js'

export function TypesTab({ data, selected }: { data: AdminMeta; selected?: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const action = useAdminAction()
  const type = data.itemTypes.find((x) => x.code === selected)
  const select = (code?: string) =>
    void navigate({ to: '/admin/meta', search: { tab: 'types', item: code } })

  return (
    <div className="grid gap-6 lg:grid-cols-[18rem_1fr]">
      <div className="grid content-start gap-3">
        <ul className="grid gap-1" aria-label={t('adminMeta.tabs.types')}>
          {data.itemTypes.map((x) => (
            <li key={x.code}>
              <button
                type="button"
                onClick={() => select(x.code)}
                aria-current={x.code === selected || undefined}
                className="flex w-full flex-wrap items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent aria-[current=true]:bg-accent"
              >
                <span className={x.active ? '' : 'text-muted-foreground line-through'}>
                  {x.name.ru}
                </span>
                <span className="font-mono text-xs text-muted-foreground">{x.code}</span>
              </button>
            </li>
          ))}
        </ul>
        <Button variant="outline" size="sm" onClick={() => select(undefined)}>
          {t('adminMeta.types.new')}
        </Button>
      </div>
      <div className="grid content-start gap-6">
        <AdminError error={action.error} />
        {type ? (
          <TypeEditor key={type.code} type={type} data={data} />
        ) : (
          <NewType data={data} onCreated={select} />
        )}
      </div>
    </div>
  )
}

function NewType({ data, onCreated }: { data: AdminMeta; onCreated: (code: string) => void }) {
  const { t } = useTranslation()
  const action = useAdminAction()
  const [code, setCode] = useState('')
  const [name, setName] = useState<LocalizedName>({ ru: '' })
  const [template, setTemplate] = useState('{title}')
  const [scale, setScale] = useState('')
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('adminMeta.types.new')}</CardTitle>
        <p className="text-sm text-muted-foreground">{t('adminMeta.types.newHint')}</p>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault()
            action.run(
              'types',
              'create',
              {
                code,
                name,
                titleTemplate: template,
                defaultGradeScale: scale || null,
              },
              () => onCreated(code),
            )
          }}
        >
          <Field label={t('adminMeta.code')} hint={t('adminMeta.codeHint')}>
            <Input
              value={code}
              required
              pattern="[a-z][a-z0-9_]*"
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <div />
          <NameInputs value={name} onChange={setName} />
          <Field label={t('admin.titleTemplate')} hint={t('adminMeta.types.templateHint')}>
            <Input value={template} required onChange={(e) => setTemplate(e.target.value)} />
          </Field>
          <Field label={t('adminMeta.types.defaultScale')}>
            <Select value={scale} onChange={(e) => setScale(e.target.value)}>
              <option value="">{t('common.notSet')}</option>
              {data.gradeScales.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name.ru}
                </option>
              ))}
            </Select>
          </Field>
          <div className="sm:col-span-2">
            <AdminError error={action.error} />
          </div>
          <Button type="submit" className="justify-self-start" disabled={action.pending}>
            {t('adminMeta.create')}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}

function TypeEditor({ type, data }: { type: AdminItemType; data: AdminMeta }) {
  const { t } = useTranslation()
  const action = useAdminAction()
  const navigate = useNavigate()
  const [name, setName] = useState(type.name)
  const [template, setTemplate] = useState(type.titleTemplate)
  const [scale, setScale] = useState(type.defaultGradeScale ?? '')
  const [fields, setFields] = useState<ItemTypeField[]>(type.fields)
  const [adding, setAdding] = useState('')
  const [saved, setSaved] = useState(false)
  const [fieldsSaved, setFieldsSaved] = useState(false)
  const byCode = new Map(data.fields.map((f) => [f.code, f]))
  const available = data.fields.filter((f) => f.active && !fields.some((x) => x.field === f.code))
  const patch = (i: number, p: Partial<ItemTypeField>) =>
    setFields(fields.map((f, k) => (k === i ? { ...f, ...p } : f)))
  const move = (i: number, d: -1 | 1) => {
    const next = [...fields]
    const j = i + d
    if (j < 0 || j >= next.length) return
    ;[next[i], next[j]] = [next[j]!, next[i]!]
    setFields(next.map((f, k) => ({ ...f, sortKey: String(k + 1).padStart(4, '0') })))
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center gap-3">
          <CardTitle className="mr-auto">
            {type.name.ru}{' '}
            <span className="font-mono text-sm text-muted-foreground">{type.code}</span>
          </CardTitle>
          <OriginBadge origin={type.origin} customized={type.customized} />
          <UsageBadge usage={type.usage} />
          <ActiveToggle
            label={type.name.ru}
            active={type.active}
            onChange={(active) => action.run('types', 'update', { code: type.code, active })}
          />
          <RemoveButton
            label={type.name.ru}
            usage={type.usage}
            onRemove={() =>
              action.run('types', 'remove', { code: type.code }, () =>
                navigate({ to: '/admin/meta', search: { tab: 'types' } }),
              )
            }
          />
        </CardHeader>
        <CardContent className="grid gap-4">
          {/* Ошибка любого действия с типом (сохранение, состав полей, выключение, удаление). */}
          <AdminError error={action.error} />
          <form
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault()
              setSaved(false)
              action.run(
                'types',
                'update',
                {
                  code: type.code,
                  name,
                  titleTemplate: template,
                  defaultGradeScale: scale || null,
                },
                () => setSaved(true),
              )
            }}
          >
            <NameInputs value={name} onChange={setName} />
            <Field label={t('admin.titleTemplate')} hint={t('adminMeta.types.templateHint')}>
              <Input value={template} required onChange={(e) => setTemplate(e.target.value)} />
            </Field>
            <Field label={t('adminMeta.types.defaultScale')}>
              <Select value={scale} onChange={(e) => setScale(e.target.value)}>
                <option value="">{t('common.notSet')}</option>
                {data.gradeScales.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name.ru}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="flex items-center gap-3 sm:col-span-2">
              <Button type="submit" disabled={action.pending}>
                {t('common.save')}
              </Button>
              {saved ? (
                <span className="text-sm text-muted-foreground">{t('profile.saved')}</span>
              ) : null}
              <Link
                to="/admin/types"
                search={{ type: type.code }}
                className="ml-auto text-sm text-primary underline underline-offset-4 hover:decoration-2"
              >
                {t('adminMeta.types.preview')}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('admin.typeFields')}</CardTitle>
          <p className="text-sm text-muted-foreground">{t('adminMeta.types.fieldsHint')}</p>
        </CardHeader>
        <CardContent className="grid gap-3">
          <Table>
            <THead>
              <Tr>
                <Th>{t('admin.refFields.name')}</Th>
                <Th>{t('adminMeta.required')}</Th>
                <Th>{t('adminMeta.section')}</Th>
                <Th />
              </Tr>
            </THead>
            <TBody>
              {fields.map((f, i) => {
                const def = byCode.get(f.field)
                const label = def?.name.ru ?? f.field
                return (
                  <Tr key={f.field}>
                    <Td>
                      <span
                        className={
                          def?.active === false ? 'text-muted-foreground line-through' : ''
                        }
                      >
                        {label}
                      </span>{' '}
                      <span className="font-mono text-xs text-muted-foreground">{f.field}</span>
                    </Td>
                    <Td>
                      <Checkbox
                        aria-label={t('adminMeta.requiredFor', { name: label })}
                        checked={f.required}
                        onChange={(e) => patch(i, { required: e.target.checked })}
                      />
                    </Td>
                    <Td>
                      <Select
                        aria-label={t('adminMeta.sectionFor', { name: label })}
                        value={f.section}
                        onChange={(e) => patch(i, { section: e.target.value as FieldSection })}
                      >
                        {FieldSection.options.map((s) => (
                          <option key={s} value={s}>
                            {t(`sections.${s}`)}
                          </option>
                        ))}
                      </Select>
                    </Td>
                    <Td className="whitespace-nowrap text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={t('adminMeta.up')}
                        onClick={() => move(i, -1)}
                      >
                        ↑
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={t('adminMeta.down')}
                        onClick={() => move(i, 1)}
                      >
                        ↓
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={t('adminMeta.removeFor', { name: label })}
                        onClick={() => setFields(fields.filter((_, k) => k !== i))}
                      >
                        ×
                      </Button>
                    </Td>
                  </Tr>
                )
              })}
            </TBody>
          </Table>
          <div className="flex flex-wrap items-end gap-2">
            <Field label={t('adminMeta.types.addField')}>
              <Select value={adding} onChange={(e) => setAdding(e.target.value)}>
                <option value="">{t('common.notSet')}</option>
                {available.map((f) => (
                  <option key={f.code} value={f.code}>
                    {f.name.ru} ({f.code})
                  </option>
                ))}
              </Select>
            </Field>
            <Button
              type="button"
              variant="outline"
              disabled={!adding}
              onClick={() => {
                const def = byCode.get(adding)!
                setFields([
                  ...fields,
                  {
                    field: adding,
                    required: false,
                    section: def.ui.section,
                    sortKey: nextKey(fields.map((f) => f.sortKey)),
                  },
                ])
                setAdding('')
              }}
            >
              {t('common.add')}
            </Button>
            <Button
              type="button"
              className="ml-auto"
              disabled={action.pending}
              onClick={() => {
                setFieldsSaved(false)
                action.run('types', 'setFields', { code: type.code, fields }, () =>
                  setFieldsSaved(true),
                )
              }}
            >
              {t('adminMeta.types.saveFields')}
            </Button>
            {fieldsSaved ? (
              <span role="status" className="w-full text-right text-sm text-muted-foreground">
                {t('adminMeta.types.fieldsSaved')}
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </>
  )
}
